import React, {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useRef,
  useState,
} from 'react';
import {
  Box,
  IconButton,
  IconButtonProps,
  Stack,
  Switch,
  TableRowProps,
} from '@mui/material';
import {
  CustomTableCell,
  CustomTableRow,
} from 'src/components/MainPage/_shared/Table/StyledTable';
import {
  ArrowDownward,
  ArrowUpward,
  Delete,
  Edit,
  ContentCopy,
  NavigateNext,
} from '@mui/icons-material';
import { ToggleButton } from './ToggleButton';
import './row-swap.css';
import { findSiblingRow, getCurrentLevelClass, getSwapClasses } from './helper';

export const SwapDirection = {
  TOP: 'top',
  BOTTOM: 'bottom',
} as const;

export type SwapDirectionType =
  typeof SwapDirection[keyof typeof SwapDirection];

type ActionsTableRowProps = TableRowProps & {
  renderCell?: (value: any) => JSX.Element;
} & {
  offsetLeft?: number | string;
  isRowDisabled?: boolean;
  isRowUpdating?: boolean;
  rowsData: ReactNode[];
  isFirst?: boolean;
  isLast?: boolean;
  isOpen?: boolean;
  extraCells?: number;
  noMutationAction?: boolean;
  onDelete?: (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onPositionChange?: (direction: SwapDirectionType) => void | Promise<any>;
  onEdit?: () => void;
  onDisable?: () => void;
  onClone?: () => void;
  onNavigate?: () => void;
};

const RowIconButton = ({ children, sx, ...rest }: IconButtonProps) => {
  return (
    <IconButton
      sx={{
        minWidth: '40px',
        height: '40px',
        svg: { fontSize: '1.25rem', color: 'secondary.light' },
        ...sx,
      }}
      {...rest}
    >
      {children}
    </IconButton>
  );
};

const RowAncestryContext = createContext(0);
const useRowAncestry = () => useContext(RowAncestryContext);
const PREFIX = 'ancestry-level';

export const ActionsTableRow = ({
  children,
  ...props
}: PropsWithChildren<ActionsTableRowProps>) => {
  const {
    sx,
    renderCell,
    className,
    offsetLeft: pl,
    isRowDisabled,
    isRowUpdating,
    rowsData,
    isFirst,
    isLast,
    isOpen: isInitialOpen,
    onDelete,
    onPositionChange,
    onEdit,
    onDisable,
    onClone,
    onNavigate,
    extraCells,
    noMutationAction = false,
    ...rest
  } = props;

  const currentRowRef = useRef<HTMLTableRowElement | null>(null);
  const [isOpen, setIsOpen] = useState(Boolean(isInitialOpen));
  const level = useRowAncestry();
  const nestingLevel = (level || 0) + 1;

  const handlePositionChange = async (direction: SwapDirectionType) => {
    if (!onPositionChange) return;

    const isTop = direction === SwapDirection.TOP;
    const currentRow = currentRowRef.current;
    const currentLevelClass = getCurrentLevelClass(currentRow, PREFIX);
    const siblingRow = findSiblingRow(currentRow, currentLevelClass, isTop);
    const [currentRowClass, siblingRowClass] = getSwapClasses(isTop);

    if (!currentRow && !siblingRow) return;

    currentRow?.classList.add(currentRowClass);
    siblingRow?.classList.add(siblingRowClass);

    setTimeout(async () => {
      try {
        await onPositionChange(direction);
      } catch (e) {
        currentRow?.classList.remove(currentRowClass);
        siblingRow?.classList.remove(siblingRowClass);
      }
    }, 200);
  };

  return (
    <RowAncestryContext.Provider value={nestingLevel}>
      <CustomTableRow
        ref={currentRowRef}
        hover
        inactive={isRowDisabled}
        {...rest}
        onAnimationStart={(e) => {
          const className = (e.target as HTMLElement)?.className;

          if (typeof className === 'string' && className?.includes(PREFIX)) {
            setIsOpen(false);
          }
        }}
        className={`${className || ''} ${PREFIX}-${nestingLevel}`.trim()}
        sx={{ ...sx, ...(pl && { 'td:first-of-type': { pl } }) }}
      >
        {rowsData.map((item, index) => {
          if (renderCell) return renderCell(item);

          return (
            <CustomTableCell key={index} sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center">
                {!index && children && (
                  <ToggleButton
                    isOpen={isOpen}
                    onToggle={() => setIsOpen((prev) => !prev)}
                  />
                )}
                {typeof item === 'string' ? (
                  <span style={{ overflowWrap: 'anywhere' }}>{item}</span>
                ) : (
                  item
                )}
              </Stack>
            </CustomTableCell>
          );
        })}
        {onPositionChange && !noMutationAction && (
          <CustomTableCell sx={{ width: '7rem', px: 0 }} align="center">
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              {!isLast && (
                <RowIconButton
                  title="Переместить вниз"
                  onClick={() => handlePositionChange(SwapDirection.BOTTOM)}
                  disabled={isRowUpdating}
                  sx={{ ...(isFirst && { mr: 6 }) }}
                >
                  <ArrowDownward />
                </RowIconButton>
              )}
              {!isFirst && (
                <RowIconButton
                  title="Переместить вверх"
                  onClick={() => handlePositionChange(SwapDirection.TOP)}
                  disabled={isRowUpdating}
                  sx={{ ...(isLast && { ml: 6 }) }}
                >
                  <ArrowUpward />
                </RowIconButton>
              )}
            </Box>
          </CustomTableCell>
        )}
        {onClone && !noMutationAction && (
          <CustomTableCell sx={{ width: '3.75rem', px: 0 }} align="center">
            <RowIconButton
              title="Клонировать"
              onClick={onClone}
              disabled={isRowUpdating}
            >
              <ContentCopy />
            </RowIconButton>
          </CustomTableCell>
        )}
        {onEdit && (
          <CustomTableCell sx={{ width: '3.75rem', px: 0 }} align="center">
            <RowIconButton
              title="Редактировать"
              onClick={onEdit}
              disabled={isRowUpdating}
            >
              <Edit />
            </RowIconButton>
          </CustomTableCell>
        )}
        {onDisable && !noMutationAction && (
          <CustomTableCell sx={{ width: '3.75rem', px: 0 }} align="center">
            <Switch checked={!isRowDisabled} onChange={onDisable} />
          </CustomTableCell>
        )}
        {onDelete && !noMutationAction && (
          <CustomTableCell sx={{ width: '3.75rem', px: 0 }} align="center">
            <RowIconButton title="Удалить" onClick={onDelete}>
              <Delete />
            </RowIconButton>
          </CustomTableCell>
        )}
        {onNavigate && (
          <CustomTableCell sx={{ width: '3.75rem', px: 0 }} align="center">
            <RowIconButton title="Смотреть детали" onClick={onNavigate}>
              <NavigateNext />
            </RowIconButton>
          </CustomTableCell>
        )}
        {extraCells && (
          <CustomTableCell colSpan={extraCells} sx={{ width: '3.75rem' }} />
        )}
      </CustomTableRow>
      {isOpen && children}
    </RowAncestryContext.Provider>
  );
};
